import React, { useState, useMemo, useCallback } from 'react';
import { useApiDebug } from '../hooks/useApiDebug';

/**
 * ApiDebugPanel - Slide-out panel for viewing API call history
 * Similar to Chrome DevTools Network tab, but simplified
 */
const ApiDebugPanel = () => {
  const { history, isOpen, clearHistory, togglePanel, closePanel } = useApiDebug();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [filterMethod, setFilterMethod] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');

  // Filter and search history
  const filteredHistory = useMemo(() => {
    return history.filter(call => {
      // Search filter
      const matchesSearch = !searchTerm ||
        call.endpoint?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.method?.toLowerCase().includes(searchTerm.toLowerCase());

      // Method filter
      const matchesMethod = filterMethod === 'ALL' || call.method === filterMethod;

      // Status filter
      let matchesStatus = true;
      if (filterStatus === '2xx') {
        matchesStatus = call.status >= 200 && call.status < 300;
      } else if (filterStatus === '4xx') {
        matchesStatus = call.status >= 400 && call.status < 500;
      } else if (filterStatus === '5xx') {
        matchesStatus = call.status >= 500;
      } else if (filterStatus === 'ERROR') {
        matchesStatus = call.error !== undefined;
      }

      return matchesSearch && matchesMethod && matchesStatus;
    });
  }, [history, searchTerm, filterMethod, filterStatus]);

  // Toggle card expansion
  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Copy to clipboard with fallback for unsupported environments
  const copyToClipboard = useCallback((text, label) => {
    // Modern clipboard API (requires HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        console.log(`✅ Copied ${label} to clipboard`);
      }).catch(err => {
        console.error('❌ Clipboard API failed:', err);
        // Try fallback method
        fallbackCopyToClipboard(text, label);
      });
    } else {
      // Fallback for older browsers or non-HTTPS contexts
      fallbackCopyToClipboard(text, label);
    }
  }, []);

  // Fallback clipboard method using execCommand
  const fallbackCopyToClipboard = (text, label) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        console.log(`✅ Copied ${label} to clipboard (fallback method)`);
      } else {
        console.error('❌ Fallback copy failed');
        alert('Copy failed. Please copy manually.');
      }
    } catch (err) {
      console.error('❌ Fallback copy error:', err);
      alert('Copy not supported in this browser. Please copy manually.');
    }
  };

  // Format JSON for display
  const formatJson = useCallback((data) => {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  }, []);

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return '#94a3b8'; // gray
    if (status >= 200 && status < 300) return '#22c55e'; // green
    if (status >= 300 && status < 400) return '#3b82f6'; // blue
    if (status >= 400 && status < 500) return '#f59e0b'; // yellow/orange
    return '#ef4444'; // red
  };

  // Get method color
  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return '#3b82f6'; // blue
      case 'POST': return '#22c55e'; // green
      case 'PUT': return '#f59e0b'; // orange
      case 'DELETE': return '#ef4444'; // red
      case 'PATCH': return '#a855f7'; // purple
      default: return '#6b7280'; // gray
    }
  };

  // Format duration
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    if (duration < 1000) return `${Math.round(duration)}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffMin < 60) return `${diffMin} minutes ago`;
    if (diffHour < 24) return `${diffHour} hours ago`;
    return `${diffDay} days ago`;
  };

  // Render API call card
  const renderApiCall = (call) => {
    const isExpanded = expandedIds.has(call.id);
    const hasQueryParams = call.queryParams && Object.keys(call.queryParams).length > 0;
    const hasPostData = call.postData && (
      typeof call.postData === 'object' ? Object.keys(call.postData).length > 0 : call.postData
    );
    const hasResponse = call.response !== undefined;

    return (
      <div key={call.id} className="api-debug-card">
        <div
          className="api-debug-card-header"
          onClick={() => toggleExpand(call.id)}
        >
          <div className="api-debug-card-top">
            <span
              className="api-debug-method-badge"
              style={{ backgroundColor: getMethodColor(call.method) }}
            >
              {call.method}
            </span>
            <span className="api-debug-endpoint" title={call.endpoint}>
              {call.endpoint}
            </span>
            <span
              className="api-debug-status"
              style={{ color: getStatusColor(call.status) }}
            >
              {call.status || (call.error ? 'ERR' : '...')}
              {call.status >= 200 && call.status < 300 ? ' ✓' : ''}
            </span>
            <button className="api-debug-expand-btn">
              {isExpanded ? '▲' : '▼'}
            </button>
          </div>
          <div className="api-debug-card-meta">
            <span>{formatDuration(call.duration)}</span>
            <span>•</span>
            <span>{formatRelativeTime(call.timestamp)}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="api-debug-card-body">
            {/* Query Parameters */}
            {hasQueryParams && (
              <div className="api-debug-section">
                <div className="api-debug-section-header">
                  <span>🔗 Query Parameters</span>
                  <button
                    className="api-debug-copy-btn"
                    onClick={() => copyToClipboard(
                      JSON.stringify(call.queryParams, null, 2),
                      'query params'
                    )}
                  >
                    📋 Copy
                  </button>
                </div>
                <pre className="api-debug-code">
                  {formatJson(call.queryParams)}
                </pre>
              </div>
            )}

            {/* POST Body */}
            {hasPostData && (
              <div className="api-debug-section">
                <div className="api-debug-section-header">
                  <span>📤 POST Body</span>
                  <button
                    className="api-debug-copy-btn"
                    onClick={() => copyToClipboard(
                      typeof call.postData === 'string' ? call.postData : formatJson(call.postData),
                      'POST body'
                    )}
                  >
                    📋 Copy JSON
                  </button>
                </div>
                <pre className="api-debug-code">
                  {formatJson(call.postData)}
                </pre>
              </div>
            )}

            {/* Response */}
            {hasResponse && (
              <div className="api-debug-section">
                <div className="api-debug-section-header">
                  <span>📥 Response</span>
                  <button
                    className="api-debug-copy-btn"
                    onClick={() => copyToClipboard(
                      formatJson(call.response),
                      'response'
                    )}
                  >
                    📋 Copy JSON
                  </button>
                </div>
                <pre className="api-debug-code">
                  {formatJson(call.response)}
                </pre>
              </div>
            )}

            {/* Error */}
            {call.error && (
              <div className="api-debug-section">
                <div className="api-debug-section-header">
                  <span>❌ Error</span>
                </div>
                <pre className="api-debug-code api-debug-error">
                  {typeof call.error === 'string' ? call.error : formatJson(call.error)}
                </pre>
              </div>
            )}

            {/* Full URL */}
            <div className="api-debug-section">
              <div className="api-debug-section-header">
                <span>🌐 Full URL</span>
                <button
                  className="api-debug-copy-btn"
                  onClick={() => copyToClipboard(call.fullUrl || call.endpoint, 'URL')}
                >
                  📋 Copy
                </button>
              </div>
              <div className="api-debug-url">
                {call.fullUrl || call.endpoint}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) {
    // Render toggle button only
    return (
      <button
        className="api-debug-toggle-btn"
        onClick={togglePanel}
        title="Open API Debug Console"
      >
        🔧 API
      </button>
    );
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div className="api-debug-backdrop" onClick={closePanel} />

      {/* Panel */}
      <div className="api-debug-panel">
        {/* Header */}
        <div className="api-debug-header">
          <h3>API Debug Console</h3>
          <button
            className="api-debug-close-btn"
            onClick={closePanel}
            title="Close panel"
          >
            ✕
          </button>
        </div>

        {/* Controls */}
        <div className="api-debug-controls">
          <div className="api-debug-control-row">
            <button
              className="api-debug-clear-btn"
              onClick={clearHistory}
              disabled={history.length === 0}
            >
              🗑️ Clear ({history.length})
            </button>
            <input
              type="text"
              className="api-debug-search"
              placeholder="🔍 Search endpoint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="api-debug-filter-row">
            <select
              className="api-debug-filter"
              value={filterMethod}
              onChange={(e) => setFilterMethod(e.target.value)}
            >
              <option value="ALL">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>

            <select
              className="api-debug-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="2xx">2xx (Success)</option>
              <option value="4xx">4xx (Client Error)</option>
              <option value="5xx">5xx (Server Error)</option>
              <option value="ERROR">Network Error</option>
            </select>
          </div>
        </div>

        {/* API Call List */}
        <div className="api-debug-list">
          {filteredHistory.length === 0 ? (
            <div className="api-debug-empty">
              {history.length === 0
                ? 'No API calls yet. Make some requests to see them here.'
                : 'No results match your filters.'}
            </div>
          ) : (
            filteredHistory.map(call => renderApiCall(call))
          )}
        </div>

        {/* Toggle button (inside panel) */}
        <button
          className="api-debug-toggle-btn api-debug-toggle-btn-open"
          onClick={togglePanel}
          title="Close API Debug Console"
        >
          🔧
        </button>
      </div>
    </>
  );
};

export default ApiDebugPanel;
